<template>
  <v-container>
    <v-card>
      <v-card-title>Search Page Views</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-menu
              ref="startDateMenu"
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="$refs.startDateMenu.save(filters.startDate)"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-menu
              ref="endDateMenu"
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="$refs.endDateMenu.save(filters.endDate)"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.path"
              label="URL Path"
              placeholder="/example/path"
              prepend-icon="mdi-link"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.ipAddress"
              label="IP Address"
              placeholder="192.168.1.1"
              prepend-icon="mdi-ip"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color=""
          :loading="loading"
          @click="searchPageViews"
          :disabled="loading"
        >
          <v-icon left>mdi-magnify</v-icon>
          <span v-if="loading">Searching...</span>
          <span v-else>Search</span>
        </v-btn>
        <v-btn class="ml-5" @click="resetFilters" outlined>
          <v-icon left>mdi-restore</v-icon>
          Reset
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card class="mt-6" v-if="hasSearched">
      <v-card-title>
        Search Results
        <v-spacer></v-spacer>
        <v-btn
          color=""
          :disabled="!pageViews.length"
          @click="exportToCSV"
        >
          <v-icon left>mdi-file-export</v-icon>
          Export to CSV
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row v-if="pageViews.length">
          <v-col cols="12" md="4">
            <v-card class="pa-3 text-center">
              <h3 class="text-lg font-semibold text-gray-700 mb-1">
                Total Results
              </h3>
              <p class="text-3xl font-bold text-blue-600">
                {{ pageViews.length }}
              </p>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pa-3 text-center">
              <h3 class="text-lg font-semibold text-gray-700 mb-1">
                Unique URLs
              </h3>
              <p class="text-3xl font-bold text-green-600">
                {{ uniqueUrlsCount }}
              </p>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="pa-3 text-center">
              <h3 class="text-lg font-semibold text-gray-700 mb-1">
                Unique IPs
              </h3>
              <p class="text-3xl font-bold text-purple-600">
                {{ uniqueIPsCount }}
              </p>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="hasSearched && !pageViews.length" class="text-center py-8">
          <p class="text-xl text-gray-500">
            No page views found matching your criteria.
          </p>
        </div>

        <v-data-table
          v-if="pageViews.length"
          :headers="headers"
          :items="pageViews"
          class="elevation-1"
        >
          <template v-slot:[`item.visited_at`]="{ item }">
            {{ formatDateTime(item.visited_at) }}
          </template>
          <template v-slot:[`item.path`]="{ item }">
            <a :href="item.path" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">
              {{ item.path }}
            </a>
          </template>
           <template v-slot:[`item.browser`]="{ item }">
            <span>{{ getBrowser(item.user_agent) }}</span>
          </template>
          <template v-slot:[`item.device`]="{ item }">
            <span>{{ getDevice(item.user_agent) }}</span>
          </template>
          <template v-slot:[`item.referrer`]="{ item }">
            <span class="truncate">{{ item.referrer || 'Direct' }}</span>
          </template>
        </v-data-table>

        <div class="mt-4 flex justify-between items-center" v-if="pageViews.length">
          <div class="text-sm text-gray-700">
            Showing <span class="font-medium">{{ pageViews.length }}</span> results
          </div>
          <div class="flex">
            <v-btn
              color=""
              class="ml-5"
              @click="loadMore"
              v-if="hasMoreResults"
            >
              Load More
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';
  import Layout from '@/Shared/VuetifyAdminLayout'
import { UAParser } from 'ua-parser-js';

export default {
  layout: Layout,
  data() {
    return {
      startDateMenu: false,
      endDateMenu: false,
      filters: {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        path: '',
        ipAddress: '',
      },
      pageViews: [],
      loading: false,
      hasSearched: false,
      hasMoreResults: false,
      currentPage: 1,
      perPage: 50,
      headers: [
        { text: 'Time', value: 'visited_at' },
        { text: 'IP Address', value: 'ip_address' },
        { text: 'URL Path', value: 'path' },
        { text: 'Browser', value: 'browser' }, // Changed from User Agent
        { text: 'Device', value: 'device' }, // New Device column
        { text: 'Referrer', value: 'referrer' },
      ],
    };
  },
  computed: {
    uniqueUrlsCount() {
      const uniqueUrls = new Set(this.pageViews.map(view => view.path));
      return uniqueUrls.size;
    },
    uniqueIPsCount() {
      const uniqueIPs = new Set(this.pageViews.map(view => view.ip_address));
      return uniqueIPs.size;
    }
  },
  methods: {
    getBrowser(userAgent) {
      if (!userAgent) return 'Unknown';
      const parser = new UAParser(userAgent);
      const result = parser.getResult();
      return result.browser.name || 'Unknown';
    },

    getDevice(userAgent) {
      if (!userAgent) return 'Unknown';
      const parser = new UAParser(userAgent);
      const result = parser.getResult();
      const deviceType = result.device.type;

      if (deviceType === 'mobile') {
        return 'Mobile';
      } else if (deviceType === 'tablet') {
        return 'Tablet';
      } else {
        return 'Desktop'; // Or more specific, e.g., result.os.name
      }
    },

    getDefaultStartDate() {
      const date = new Date();
      date.setDate(date.getDate() - 7); // 7 days ago
      return date.toISOString().split('T')[0];
    },
    getDefaultEndDate() {
      return new Date().toISOString().split('T')[0]; // Today
    },
    async searchPageViews() {
      this.loading = true;
      this.currentPage = 1;

      try {
        const response = await axios.get('/admin/analytics/search-page-views', {
          params: {
            ...this.filters,
            page: this.currentPage,
            perPage: this.perPage
          }
        });

        this.pageViews = response.data.data;
        this.hasMoreResults = response.data.total > (this.currentPage * this.perPage);
        this.hasSearched = true;
      } catch (error) {
        console.error('Error searching page views:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.loading = true;
      this.currentPage++;

      try {
        const response = await axios.get('/admin/analytics/search-page-views', {
          params: {
            ...this.filters,
            page: this.currentPage,
            perPage: this.perPage
          }
        });

        this.pageViews = [...this.pageViews, ...response.data.data];
        this.hasMoreResults = response.data.total > (this.currentPage * this.perPage);
      } catch (error) {
        console.error('Error loading more page views:', error);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        path: '',
        ipAddress: '',
      };
      this.pageViews = [];
      this.hasSearched = false;
    },

    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString();
    },

    exportToCSV() {
      if (!this.pageViews.length) return;

      // Define CSV headers
      const headers = ['Date/Time', 'IP Address', 'URL Path', 'Browser', 'Device', 'Referrer'];

      // Convert data to CSV format
      const csvData = this.pageViews.map(view => [
        this.formatDateTime(view.visited_at),
        view.ip_address,
        view.path,
        this.getBrowser(view.user_agent),
        this.getDevice(view.user_agent),
        view.referrer || 'Direct'
      ]);
      
      // Add headers to the beginning
      csvData.unshift(headers);
      
      // Convert to CSV string
      const csvContent = csvData.map(row => row.map(cell => 
        `"${String(cell).replace(/"/g, '""')}"`
      ).join(',')).join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `page-views-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  mounted() {
    // You could auto-search on page load if desired
    // this.searchPageViews();
  }
};
</script>
<style scoped>
/* Add any component-specific styles here */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
